import useCodesSidebar from 'hooks/codes/useCodesSidebar';
import { useSurveyList } from 'hooks/surveys/queries';
import { useProject } from 'hooks/projects/queries';
import { useAllProjectTranscripts } from 'hooks/transcripts/queries';
import { useFilterReport } from 'hooks/excerpts/queries';
import { useDescriptors } from 'hooks/descriptors/queries';

/**
 * Custom hook to fetch all data needed for filtering
 * @param {string|number} projectID - The project ID
 * @param {Object} filters - Filter object containing codes, transcripts, surveys, etc.
 * @param {string} source - Source type ('snippets' or 'chat')
 * @returns {Object} All data needed for filtering with loading states
 */
const useFilterData = (projectID, filters, source = 'snippets') => {
  // Get codes data
  const { codes, isLoading: codesLoading, error: codesError } = useCodesSidebar(projectID);

  // Get surveys data
  const { data: surveys, isLoading: surveysLoading, error: surveysError } = useSurveyList(projectID);

  // Get project data (without transcripts)
  const { data: project, isLoading: projectLoading, error: projectError } = useProject(projectID);

  // Get transcripts separately
  const { data: transcripts, isLoading: transcriptsLoading, error: transcriptsError } = useAllProjectTranscripts(projectID);

  // Get filter report data which contains frequencies
  const { data: filterReport, isLoading: filterReportLoading, error: filterReportError } = useFilterReport(
    projectID,
    filters,
    source
  );

  // Get descriptors using the existing hook
  const {
    data: descriptorsData,
    isLoading: descriptorsLoading,
    error: descriptorsError
  } = useDescriptors(projectID);

  // The actual frequency data comes from filterReport
  // We don't need to calculate it separately

  // Collect any errors
  const error = codesError || surveysError || projectError || transcriptsError || filterReportError || descriptorsError;

  return {
    codes,
    surveys,
    project: { ...project, transcripts: transcripts || [] }, // Add transcripts to project for backward compatibility
    filterReport,
    descriptorsData: descriptorsData?.descriptors || [],
    isLoading: codesLoading || surveysLoading || projectLoading || transcriptsLoading || descriptorsLoading,
    error
  };
};

export default useFilterData;
